import { css } from '@emotion/react';
import {
  Box,
  Paper,
  Autocomplete,
  TextField,
  Typography,
  PaperProps,
  Link,
} from '@mui/material';
import { ClubLogo } from '@scorenco/components';
import { CrispService, ClubSearchHit } from '@scorenco/core';
import { useElasticSearch } from '@/components/Search';

type SearchClubProps = PaperProps & {
  sport: string;
};

export const SearchClub = ({ sport, ...rest }: SearchClubProps) => {
  const { hits, search, loading, query } = useElasticSearch();

  // On mount, trigger a search with empty term to get default sport results if needed
  // Alternatively, just rely on user typing.
  // The original component did setFilters({ sport_slug: [sport] }) which might have been for the index context.

  // Since we use the provider's hits, we might want to cast them to ClubSearchHit[] if we're sure
  const clubHits = (hits || []) as ClubSearchHit[];

  // Handlers for Autocomplete
  const handleInputChange = (_event: unknown, value: string) => {
    // In the new provider, search takes { term, ... }
    // We explicitly pass the sport filter here
    search({ term: value, sport_slug: sport, type: 'club' });
  };

  return (
    <Paper variant="elevation" elevation={4} {...rest}>
      <Autocomplete
        fullWidth
        popupIcon={null}
        options={clubHits}
        loading={loading}
        filterOptions={(options) => options} // Disable client-side filtering, let API handle it
        getOptionLabel={(option) => option.name || query}
        noOptionsText={
          <Box>
            Vous ne trouvez pas votre club ?{' '}
            <Link onClick={() => CrispService.show()}>Contactez-nous</Link>
          </Box>
        }
        renderOption={(props, option) => {
          if (!option?.slug) {
            return null;
          }

          // Compatibilité: utilisation de id ou objectID
          const clubId =
            option.id || (option.objectID ? parseInt(option.objectID) : 0);

          return (
            <Box component="li" {...props}>
              <Box
                component="a"
                sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                href={`/${option?.sport_slug}/clubs/${option?.slug}-${clubId.toString(36)}`}
              >
                <ClubLogo
                  logo={option.logo}
                  css={css`
                    width: 2rem;
                    height: 2rem;
                    margin-right: 1rem;
                    margin-top: 0.5rem;
                    margin-bottom: 0.5rem;
                  `}
                  width={32}
                  height={32}
                />
                <Typography sx={{ width: '100%' }} component="span">
                  {option.name}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            variant="outlined"
            label="Entrez le nom de votre club favori ou de votre ville"
            onChange={(_event) => {
              // Handled by onInputChange mostly, but onChange is for selection
              // Autocomplete has onInputChange for text changes
            }}
            {...params}
            // Overriding onChange with onInput for typing detection
            onInput={(e: any) => handleInputChange(e, e.target.value)}
          />
        )}
      />
    </Paper>
  );
};
